import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { editStatus } from "../../redux/state/status";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { useSocket } from '../../hook/useSocket';
import { addDialog } from '../../redux/state/dialog';
import { useCacheService } from '../../hook/useCacheService';
import { store } from '../../redux/store';
import { shouldShowWebToCaseMaintenance } from '../../utils/webToCaseMaintenance';



const QueueProcessingDialog = () => {
    const socket = useSocket();
    const { endSession } = useCacheService();
    const navigate = useNavigate();

    let dispatch = useDispatch();
    const {t} = useTranslation();

    const chatId = useSelector(
        (state: { session: { chatId: string } }) => state.session.chatId
    );

    const sessionId = useSelector(
        (state: { session: { sessionId: string } }) => state.session.sessionId
    );

    const username = useSelector(
        (state: { session: { username: string } }) => state.session.username
    );

    const botFontSize = useSelector(
        (state: { settings: { botFontSize: number } }) =>
            state.settings.botFontSize
    );

    const botButtonColor = useSelector(
        (state: { settings: { botButtonColor: string } }) => state.settings.botButtonColor
    );

    const queueDialogColor = useSelector(
        (state: { settings: { queueDialogColor: string } }) => state.settings.queueDialogColor
    );

    const queueBackgroundColor = useSelector(
        (state: { settings: { queueBackgroundColor: string } }) => state.settings.queueBackgroundColor
    );

    const isChatAlive = useSelector(
        (state: { status: { isChatAlive: boolean } }) => state.status.isChatAlive
    );

    const queueStatus = useSelector(
        (state: { status: { queueStatus: String } }) => state.status.queueStatus
    );

    var queueNumber = useSelector(
        (state: { status: { queueNumber: number } }) => state.status.queueNumber
    );
    const queueMessage = useSelector(
        (state: { systemMessages: { queue_message: { message: String } } }) =>
            state.systemMessages.queue_message.message
    );
    const queueGreetingMessage = useSelector(
        (state: {
            systemMessages: { queue_greeting_message: { message: String } };
        }) => state.systemMessages.queue_greeting_message.message
    );

    const queueOfflineFormButton = useSelector(
        (state: {
            systemMessages: { queue_offline_form_button: { message: String } };
        }) => state.systemMessages.queue_offline_form_button.message
    );

    const enableOfflineForm = useSelector(
        (state: { settings: { enableOfflineForm: Boolean } }) =>
            state.settings.enableOfflineForm
    );

    const useExternalLinkForOfflineForm = useSelector(
        (state: { settings: { useExternalLinkForOfflineForm: Boolean } }) =>
            state.settings.useExternalLinkForOfflineForm
    );

    const externalLinkForOfflineForm = useSelector(
        (state: { settings: { externalLinkForOfflineForm: string } }) =>
            state.settings.externalLinkForOfflineForm
    );

    const customValues = useSelector(
        (state: { settings: { customValues: any } }) => state.settings.customValues
    );

    const conversationId = useSelector(
        (state: { session: { conversationId: string } }) =>
            state.session.conversationId
    );

    const accessToken = useSelector(
        (state: { session: { accessToken: string } }) =>
            state.session.accessToken
    );

    const isAuthenticated = useSelector(
        (state: { session: { isAuthenticated: any } }) =>
            state.session.isAuthenticated
    );

    const memberCode = useSelector(
        (state: { session: { memberCode: string } }) => state.session.memberCode
    );

    const brandCode = useSelector(
        (state: { settings: { brandCode: string } }) => state.settings.brandCode
    );
    
    let parsedValues = customValues ? JSON.parse(customValues) : {};
    const webToCaseForm = parsedValues.hasOwnProperty('web_to_case_form') ? parsedValues.web_to_case_form : 'web_to_case'
    const isVipBot = String(parsedValues?.is_vip_bot ?? 'false').toLowerCase() === 'true';
    const isAffBot = String(parsedValues?.is_aff_bot ?? 'false').toLowerCase() === 'true';
    const liveAgentMode = parsedValues.hasOwnProperty('live_agent_mode')
        ? parsedValues.live_agent_mode
        : '0';

    const queueETA = Number(parsedValues?.queue_eta ?? 2);

    const fontSizesRef = ['sm', 'sm', 'sm', 'lg', 'xl'];
    const smallFontSizeRef = ['xs', 'xs', 'xs', 'base', 'lg'];
    const fontSizes = [
        fontSizesRef[0 + botFontSize],
        fontSizesRef[1 + botFontSize],
        fontSizesRef[2 + botFontSize],
    ];
    const smallFontSizes = [
        smallFontSizeRef[0 + botFontSize],
        smallFontSizeRef[1 + botFontSize],
        smallFontSizeRef[2 + botFontSize],
    ];

    // Larger sizing for the queue position number (keeps the number more prominent than the label).
    // Kept as literal Tailwind classes to avoid purge issues.
    const positionNumberSizeClass = (() => {
        const idx = Math.max(0, Math.min(4, Number(botFontSize) || 0));
        const map = [
            "text-3xl small:text-3xl medium:text-4xl large:text-5xl",
            "text-3xl small:text-3xl medium:text-4xl large:text-5xl",
            "text-4xl small:text-4xl medium:text-5xl large:text-6xl",
            "text-5xl small:text-5xl medium:text-6xl large:text-6xl",
            "text-6xl small:text-6xl medium:text-6xl large:text-6xl",
        ];
        return map[idx];
    })();

    const getValidColor = (color?: unknown, fallback = "#16a34a") =>
        typeof color === "string" && color.length >= 7
          ? color.slice(0, 7)
          : fallback;
      
    const themeColor = getValidColor(
        queueDialogColor,
        getValidColor(botButtonColor)
    );
      

    function pastelizeHex(
        hex: string,
        lightnessBoost = 35,
        saturationReduce = 15
    ): string {
        hex = hex.replace('#', '');

        let r = parseInt(hex.slice(0, 2), 16) / 255;
        let g = parseInt(hex.slice(2, 4), 16) / 255;
        let b = parseInt(hex.slice(4, 6), 16) / 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h = 0,
            s = 0,
            l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

            switch (max) {
                case r:
                    h = (g - b) / d + (g < b ? 6 : 0);
                    break;
                case g:
                    h = (b - r) / d + 2;
                    break;
                case b:
                    h = (r - g) / d + 4;
                    break;
            }
            h /= 6;
        }

        l = Math.min(1, l + lightnessBoost / 100);
        s = Math.max(0, s - saturationReduce / 100);

        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        let r2, g2, b2;
        if (s === 0) {
            r2 = g2 = b2 = l;
        } else {
            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r2 = hue2rgb(p, q, h + 1 / 3);
            g2 = hue2rgb(p, q, h);
            b2 = hue2rgb(p, q, h - 1 / 3);
        }

        return (
            '#' +
            Math.round(r2 * 255).toString(16).padStart(2, '0') +
            Math.round(g2 * 255).toString(16).padStart(2, '0') +
            Math.round(b2 * 255).toString(16).padStart(2, '0')
        );
    }

        
    const lightThemeColor = typeof queueBackgroundColor === "string" && queueBackgroundColor.length >= 7
            ? queueBackgroundColor.slice(0, 7)
            : pastelizeHex(themeColor);

    function formatMinutesToLocalizedDuration(
        minutes: number,
        opts: { minuteUnit: string; secondUnit: string }
    ): string {
        if (!Number.isFinite(minutes) || minutes < 0) return "?";
        const totalSeconds = Math.max(0, Math.round(minutes * 60));
        const mm = Math.floor(totalSeconds / 60);
        const ss = totalSeconds % 60;

        if (mm <= 0) return `${ss} ${opts.secondUnit}`;
        if (ss <= 0) return `${mm} ${opts.minuteUnit}`;
        return `${mm} ${opts.minuteUnit} ${ss} ${opts.secondUnit}`;
    }

    const minuteUnit = t("queueProcessingDialog.minuteUnit", { defaultValue: "min" });
    const secondUnit = t("queueProcessingDialog.secondUnit", { defaultValue: "sec" });

    const waitMinutes =
        queueNumber != null
            ? formatMinutesToLocalizedDuration(queueETA * (queueNumber + 1), {
                  minuteUnit,
                  secondUnit,
              })
            : "?";

    const handleCancelQueue = () => {

        console.log("Web-to-Case button pressed during queue page");

        let logQueueNumber = queueNumber + 1;

        socket.sendUploadChatEvent({
            chatId: chatId,
            speaker: 'Client',
            speakerName: username,
            datetime: new Date().toJSON(),
            message: "Web-to-Case button pressed during queue page. Member queue position at " + logQueueNumber.toString(),
            is_system: true,
        });


        dispatch(editStatus({ name: 'queueStatus', value: 'canceled' }));

        const sfAvailability = store.getState().settings.sfAvailability;
        if (shouldShowWebToCaseMaintenance(sfAvailability)) {
            if (liveAgentMode == '1' && conversationId && accessToken) {
                socket.sendChatEndEvent({
                    chatId: chatId,
                    conversationId: conversationId,
                    accessToken: accessToken,
                    liveAgentFlag: liveAgentMode,
                    isAuthenticated: isAuthenticated,
                    memberCode: memberCode,
                    brandCode: brandCode,
                    isVipBot: isVipBot,
                    isAffBot: isAffBot,
                });
            } else {
                console.error(
                    'leave queue (maintenance): missing MIAW conversationId/accessToken or not live_agent_mode=1; navigating without chasitorChatEnd'
                );
            }
            endSession(sessionId);
            socket.sendUploadChatEvent({
                chatId: chatId,
                speaker: 'Client',
                speakerName: username,
                datetime: new Date().toJSON(),
                message:
                    'Web-to-Case skipped (QueueProcessingDialog): sf_availability.available=false; redirected to maintenance',
                is_system: true,
            });
            navigate('/maintenance', { replace: true });
            return;
        }

        dispatch(
            addDialog({
                task: 'ended',
                type: 'action',
            })
        );
        dispatch(
            addDialog({
                task: 'offlineForm',
                type: 'action',
            })
        );
        endSession(sessionId);
        if (webToCaseForm == "chat_rating"){
            dispatch(editStatus({ name: 'isWebToCase', value: true }));
            dispatch(
                editStatus({
                    name: 'isChatRatingModal',
                    value: true,
                })
            );
        } else{
            dispatch(
                editStatus({
                    name: 'isIframeModal',
                    value:
                        true && enableOfflineForm && useExternalLinkForOfflineForm,
                })
            );
            dispatch(
                editStatus({
                    name: 'iframeUrl',
                    value: externalLinkForOfflineForm,
                })
            );
        }

    };

    return (
        <div
            style={{ fontFamily: "'Times New Roman', Times, serif" }}
            className={classNames(
                "w-90pc medium:w-90pc absolute bottom-[12px] left-1/2 transform -translate-x-1/2 text-sm z-10",
                {
                    hidden: queueStatus == null || queueStatus == 'canceled' || queueStatus == 'established' || queueStatus == 'failed' || queueStatus == 'loading' || queueStatus == 'queueing' || !isChatAlive || isVipBot
                }
            )}
        >
             {(queueStatus === 'queueingDialog' || queueStatus === 'queueLoading' ) && (
                <>
                    <div
                        className="flex items-stretch gap-3 rounded-2xl border px-3 py-2 shadow-sm backdrop-blur"
                        style={{
                            borderColor: themeColor,
                            backgroundColor: lightThemeColor,
                        }}
                    >
                        <div
                            className="flex w-[16%] self-stretch flex-none flex-col items-center justify-center rounded-xl text-white text-center"
                            style={{ backgroundColor: themeColor }}
                        >
                            {queueStatus === 'queueLoading' &&
                                (
                                    <>
                                    <div className="flex justify-center">
                                        <svg
                                        role="status"
                                        className={classNames(
                                            'inline w-10 h-10 mr-2 text-gray-200 animate-spin',
                                        )}
                                        // className="inline w-10 h-10 mr-2 text-gray-200 animate-spin fill-sky-500"
                                        viewBox="0 0 100 101"
                                        fill={botButtonColor}
                                        xmlns="http://www.w3.org/2000/svg"
                                        >
                                        <path
                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                            fill="currentColor"
                                        ></path>
                                        <path
                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                            fill="currentFill"
                                        ></path>
                                        </svg>
                                    </div>
                                    </>
                                )}
                            {queueStatus === 'queueingDialog' && (
                                <>
                                    <div
                                        className={classNames("truncate font-bold leading-none mb", positionNumberSizeClass)}
                                    >
                                        {queueNumber + 1}
                                    </div>
                                    <div className={`mt-2 w-full text-center text-${smallFontSizes[0]} small:text-${smallFontSizes[0]} medium:text-${smallFontSizes[1]} large:text-${smallFontSizes[2]} leading-none`}>
                                        {t("queueProcessingDialog.positionLabel")}
                                    </div>
                                </>
                             )}
                        </div>

                        <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className={`truncate text-${fontSizes[0]} small:text-${fontSizes[0]} medium:text-${fontSizes[1]} large:text-${fontSizes[2]} font-bold text-black`}>
                                        {queueGreetingMessage ?? t("queueProcessingDialog.queueGreetingMessage")}
                                    </div>
                                    <div className={`mt-0.5 text-${fontSizes[0]} small:text-${fontSizes[0]} medium:text-${fontSizes[1]} large:text-${fontSizes[2]} leading-snug text-black`}>
                                        {queueMessage ?? t("queueProcessingDialog.queueMessage")}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleCancelQueue}
                                    aria-label={t("queueProcessingDialog.close")}
                                    className="
                                        -mt-2 -mr-2 inline-flex h-8 w-8 items-center justify-center rounded-lg
                                        text-black
                                        hover:bg-black/10
                                        active:bg-black/20
                                        focus:outline-none
                                        focus-visible:ring-2 focus-visible:ring-black/40
                                        transition
                                    "
                                >
                                    <FontAwesomeIcon icon={faRightFromBracket} aria-hidden="true" />
                                </button>
                            </div>
                            {queueStatus === 'queueingDialog' && (
                                <>
                                    <div
                                        className={`mt-2 flex items-center text-${fontSizes[0]} small:text-${fontSizes[0]} medium:text-${fontSizes[1]} large:text-${fontSizes[2]}`}
                                        style={{ color: themeColor }}
                                    >
                                        <span className="inline-flex items-center justify-center">
                                            <FontAwesomeIcon icon={faClock} />
                                        </span>
                                        <span className="font-bold ml-2">
                                            {t("queueProcessingDialog.eta", {waitMinutes})}
                                        </span>
                                    </div>
                                </>
                             )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
    
};

QueueProcessingDialog.propTypes = {};

export default QueueProcessingDialog;
